import { useState } from "react";
import api, { apiError, brl, imgUrl, maskDoc } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Tag, Pencil, Trash2, Plus, Upload, FileText, X, Users } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import PurchaseHistory from "./crm/PurchaseHistory";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Badge, Button, DataTable, Field, FilterSheet, Input, PageHeader, SearchInput, Select, Textarea } from "@/components/ui-kit";

const SUGGESTED_TAGS = ["pós-cirúrgico", "convênio", "recorrente", "idoso", "atleta"];
const EMPTY_ADDRESS = { cep: "", street: "", number: "", complement: "", district: "", city: "", state: "" };
const EMPTY_FORM = { name: "", email: "", phone: "", document: "", rg: "", birthdate: "", gender: "", notes: "", password: "", address: { ...EMPTY_ADDRESS } };
const DOC_TYPES = ["RG", "CPF", "Receita médica", "Laudo", "Comprovante", "Outro"];
const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];
const SITUACOES = [["com_pedidos", "Com pedidos"], ["sem_pedidos", "Sem pedidos"]];
const COMPRAS = [["30d", "Comprou nos últimos 30 dias"], ["90d", "Comprou nos últimos 90 dias"], ["inativos", "Inativos (+180 dias)"], ["nunca", "Nunca compraram"]];

const toForm = (d) => ({
  name: d.name || "", email: (d.email || "").endsWith("@local.ortopedicasp") ? "" : (d.email || ""), phone: d.phone || "",
  document: d.document ? maskDoc(d.document) : "", rg: d.rg || "", birthdate: d.birthdate ? d.birthdate.slice(0, 10) : "",
  gender: d.gender || "", notes: d.notes || "", password: "",
  address: d.addresses?.[0]
    ? { cep: d.addresses[0].cep || "", street: d.addresses[0].street || "", number: d.addresses[0].number || "", complement: d.addresses[0].complement || "", district: d.addresses[0].district || "", city: d.addresses[0].city || "", state: (d.addresses[0].state || "").trim() }
    : { ...EMPTY_ADDRESS },
});

function TabBtn({ id, tab, setTab, children }) {
  return (
    <button
      onClick={() => setTab(id)}
      type="button"
      role="tab"
      aria-selected={tab === id}
      data-testid={`crm-tab-${id}`}
      /* `shrink-0` e `whitespace-nowrap` são o que mantém a fila numa linha só
         dentro do trilho rolável: sem eles as abas se espremiam e "Dados
         cadastrais" quebrava em duas linhas, desalinhando a fileira inteira.
         44px de altura no toque, a densidade antiga no mouse. */
      className={`shrink-0 whitespace-nowrap px-3.5 h-11 sm:h-8 text-sm font-semibold rounded-lg transition-colors ${
        tab === id ? "bg-brand-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );
}

/** Atalho local para manter a escrita curta nos formulários desta tela. */
function Fld({ label, children, ...rest }) {
  return <Field label={label} {...rest}>{children}</Field>;
}

export default function CRM() {
  const [tagging, setTagging] = useState(null);
  const [tags, setTags] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | customer
  const [tab, setTab] = useState("dados");
  const [form, setForm] = useState(EMPTY_FORM);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("RG");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [savingTags, setSavingTags] = useState(false);

  const [tag, setTag] = useState("");
  const [situacao, setSituacao] = useState("");
  const [compra, setCompra] = useState("");
  const [uf, setUf] = useState("");

  // Busca e filtros no servidor. Antes filtrava em memória sobre os 300 clientes
  // carregados — quem estivesse fora deles era invisível.
  const {
    items: customers, total, page, lastPage, setPage, search, setSearch, sort, setSort,
    isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/admin/customers", {
    tag: tag || undefined,
    situacao: situacao || undefined,
    compra: compra || undefined,
    uf: uf || undefined,
  });

  const hasFilters = !!(tag || situacao || compra || uf);
  const clearFilters = () => { setTag(""); setSituacao(""); setCompra(""); setUf(""); };

  const openNew = () => { setForm({ ...EMPTY_FORM, address: { ...EMPTY_ADDRESS } }); setDocs([]); setTab("dados"); setEditing("new"); };
  const openEdit = async (c) => {
    setEditing(c); setTab("dados"); setForm({ ...EMPTY_FORM, address: { ...EMPTY_ADDRESS } }); setDocs([]);
    try {
      const { data } = await api.get(`/admin/customers/${c.id}`);
      setForm(toForm(data));
      setDocs(data.documents || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setAddr = (k) => (e) => setForm({ ...form, address: { ...form.address, [k]: e.target.value } });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      const payload = { ...form, document: form.document || null, birthdate: form.birthdate || null };
      if (editing === "new") {
        payload.password = form.password || null;
        await api.post("/admin/customers", payload);
        toast.success("Cliente cadastrado");
      } else {
        delete payload.password;
        await api.patch(`/admin/customers/${editing.id}`, payload);
        toast.success("Cliente atualizado");
      }
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || editing === "new") return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: doc } = await api.post(`/admin/customers/${editing.id}/documents`, { type: docType, url: up.url, filename: file.name });
      setDocs((d) => [doc, ...d]);
      toast.success("Documento anexado");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadingDoc(false); e.target.value = ""; }
  };

  const removeDoc = async (doc) => {
    try { await api.delete(`/admin/customers/${editing.id}/documents/${doc.id}`); setDocs((d) => d.filter((x) => x.id !== doc.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/admin/customers/${c.id}`); toast.success("Cliente excluído"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveTags = async () => {
    const list = tags.split(",").map((t) => t.trim()).filter(Boolean);
    setSavingTags(true);
    try { await api.patch(`/admin/customers/${tagging.id}/tags`, { tags: list }); toast.success("Tags atualizadas"); setTagging(null); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSavingTags(false); }
  };

  // Os mesmos controles servem à linha (desktop) e à folha (mobile) — declarados
  // uma vez para não divergirem.
  const filtros = (
    <>
      <Select value={tag} onChange={(e) => setTag(e.target.value)} data-testid="crm-tag-filter"
        aria-label="Filtrar por tag" className="w-auto px-2.5 text-slate-600">
        <option value="">Todas as tags</option>
        {SUGGESTED_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
      </Select>
      <Select value={situacao} onChange={(e) => setSituacao(e.target.value)} data-testid="crm-situacao-filter"
        aria-label="Filtrar por situação" className="w-auto px-2.5 text-slate-600">
        <option value="">Todas as situações</option>
        {SITUACOES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
      <Select value={compra} onChange={(e) => setCompra(e.target.value)} data-testid="crm-compra-filter"
        aria-label="Filtrar por período de compra" className="w-auto px-2.5 text-slate-600">
        <option value="">Todas as compras</option>
        {COMPRAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
      <Select value={uf} onChange={(e) => setUf(e.target.value)} data-testid="crm-uf-filter"
        aria-label="Filtrar por UF" className="w-auto px-2.5 text-slate-600">
        <option value="">Todas as UFs</option>
        {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
      </Select>
      {hasFilters && (
        <Button variant="link" size="sm" icon={X} onClick={clearFilters} data-testid="clear-filters-btn">Limpar filtros</Button>
      )}
    </>
  );
  // Quantos filtros estão aplicados — vira o número no botão da folha.
  const filtrosAtivos = [tag, situacao, compra, uf].filter(Boolean).length;

  return (
    <div className="space-y-5" data-testid="admin-crm-page">
      <PageHeader
        eyebrow="Relacionamento"
        title="Clientes"
        icon={Users}
        description="Base única da loja e do balcão. Documentos anexados aqui aparecem na OS e no contrato de aluguel."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, e-mail, telefone ou CPF" data-testid="crm-search-input" aria-label="Buscar cliente por nome, e-mail, telefone ou CPF" width="w-72" />
            <Button icon={Plus} onClick={openNew} data-testid="crm-new-btn">Novo cliente</Button>
          </>
        )}
        filters={filtros}
        mobileFilters={(
          <FilterSheet count={filtrosAtivos} onClear={clearFilters}>{filtros}</FilterSheet>
        )}
      />


      <DataTable
        caption="Clientes cadastrados"
        rows={customers}
        sort={sort}
        onSortChange={setSort}
        rowKey={(c) => c.id}
        loading={loading}
        error={error}
        onRetry={load}
        empty={{
          icon: Users,
          title: search || hasFilters ? "Nenhum cliente com esses critérios" : "Nenhum cliente ainda",
          desc: search || hasFilters
            ? "Ajuste a busca ou limpe os filtros aplicados."
            : "Clientes entram por aqui, pelo cadastro da loja ou pelo atendimento no balcão.",
          action: (search || hasFilters)
            ? <Button variant="secondary" onClick={clearFilters}>Limpar filtros</Button>
            : <Button icon={Plus} onClick={openNew}>Cadastrar cliente</Button>,
          testId: "crm-empty",
        }}
        columns={[
          {
            key: "cliente", header: "Cliente", sortable: true,
            cell: (c) => (
              <>
                <p className="font-medium text-slate-900">{c.name}</p>
                <p className="text-xs text-slate-400">{c.email?.endsWith("@local.ortopedicasp") ? "sem e-mail" : c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                <p className="text-xs text-slate-400">{c.document ? maskDoc(c.document) : "sem CPF"}{c.city ? ` · ${c.city}` : ""}</p>
              </>
            ),
          },
          {
            key: "tags", header: "Tags",
            cell: (c) => (
              <div className="flex flex-wrap gap-1">
                {(c.tags || []).map((t) => <Badge key={t} tone="info">{t}</Badge>)}
                {(!c.tags || c.tags.length === 0) && <span className="text-xs text-slate-300">—</span>}
              </div>
            ),
          },
          { key: "pedidos", header: "Pedidos", align: "center", mono: true, sortable: true, sortDir: "desc", cell: (c) => c.orders_count },
          { key: "gasto", header: "Total gasto", align: "right", mono: true, sortable: true, sortDir: "desc", className: "font-semibold", cell: (c) => brl(c.total_spent) },
          {
            key: "ultima", header: "Última compra", sortable: true, sortDir: "desc", className: "text-slate-500",
            cell: (c) => (c.last_order ? new Date(c.last_order).toLocaleDateString("pt-BR") : "—"),
          },
          {
            key: "acoes", header: "Ações", align: "center", className: "whitespace-nowrap",
            cell: (c) => (
              <>
                <Button variant="ghost" size="iconSm" onClick={() => openEdit(c)} data-testid="crm-edit-btn"
                  aria-label={`Editar ${c.name}`} title="Editar cliente" className="hover:text-brand-600"><Pencil size={15} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => { setTagging(c); setTags((c.tags || []).join(", ")); }}
                  data-testid="crm-tags-btn" aria-label={`Editar tags de ${c.name}`} title="Editar tags" className="hover:text-brand-600"><Tag size={15} /></Button>
                {c.phone && (
                  <a href={`https://wa.me/${c.phone.replace(/\D/g, "")}?text=Olá ${c.name}! Aqui é da Ortopédica São Paulo.`}
                    target="_blank" rel="noreferrer" data-testid="crm-whatsapp-btn"
                    aria-label={`Chamar ${c.name} no WhatsApp`} title="Chamar no WhatsApp"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"><MessageCircle size={15} /></a>
                )}
                <Button variant="ghost" size="iconSm" onClick={() => setRemoving(c)} data-testid="crm-delete-btn"
                  aria-label={`Excluir ${c.name}`} title="Excluir cliente" className="hover:text-red-500"><Trash2 size={15} /></Button>
              </>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="clientes" />

      <ConfirmDialog open={!!removing} onClose={() => setRemoving(null)} onConfirm={() => remove(removing)}
        title="Excluir cliente" message={removing ? `Excluir ${removing.name}? Endereços, documentos e carrinho serão removidos. Esta ação não pode ser desfeita.` : ""} confirmLabel="Excluir" />

      {editing && (
        <Modal
          open
          onClose={() => setEditing(null)}
          title={editing === "new" ? "Novo cliente" : `Editar ${editing.name}`}
          maxWidth="max-w-2xl"
          testId="crm-form-dialog"
          /* No rodapé fixo, e não no fim do formulário: com quatro abas e um
             cadastro longo, salvar era o botão mais importante da tela e o mais
             difícil de alcançar — só aparecia depois de rolar tudo.
             Compras é aba de LEITURA: ali o botão sairia como se houvesse algo a
             gravar, e o que ele gravaria (o cadastro) nem está na tela. */
          footer={tab !== "compras" && (
            <button onClick={save} disabled={saving || !form.name} data-testid="crm-form-save"
              className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white font-semibold h-12 sm:h-11 rounded-lg transition-colors">
              {saving ? "Salvando..." : editing === "new" ? "Cadastrar cliente" : "Salvar alterações"}
            </button>
          )}
        >
            {/* Trilho PRÓPRIO de rolagem. Antes a fila media ~450px num modal de
                358px e transbordava para o contêiner, que arrastava o formulário
                inteiro para o lado. Contida aqui, quem rola é só a fila.
                `-mx-5` sangra até a borda da folha, para a última aba não parecer
                cortada por engano. */}
            <div
              role="tablist"
              aria-label="Seções do cadastro"
              className="flex gap-1.5 mb-4 border-b border-slate-100 pb-3 overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <TabBtn id="dados" tab={tab} setTab={setTab}>Dados cadastrais</TabBtn>
              <TabBtn id="endereco" tab={tab} setTab={setTab}>Endereço</TabBtn>
              <TabBtn id="docs" tab={tab} setTab={setTab}>Documentação</TabBtn>
              {/* Só para cliente já salvo: não há compras de quem ainda não existe. */}
              {editing !== "new" && <TabBtn id="compras" tab={tab} setTab={setTab}>Compras</TabBtn>}
            </div>

            {tab === "dados" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Fld label="Nome completo"><Input value={form.name} onChange={set("name")} data-testid="crm-form-name" /></Fld></div>
                <Fld label="E-mail"><Input value={form.email} onChange={set("email")} type="email" placeholder="opcional" data-testid="crm-form-email" /></Fld>
                <Fld label="Telefone / WhatsApp"><Input value={form.phone} onChange={set("phone")} data-testid="crm-form-phone" /></Fld>
                <Fld label="CPF / CNPJ"><Input value={form.document} onChange={(e) => setForm({ ...form, document: maskDoc(e.target.value) })} data-testid="crm-form-document" mono /></Fld>
                <Fld label="RG"><Input value={form.rg} onChange={set("rg")} /></Fld>
                <Fld label="Nascimento"><Input type="date" value={form.birthdate} onChange={set("birthdate")} data-testid="crm-form-birthdate" /></Fld>
                <Fld label="Gênero">
                  <Select value={form.gender} onChange={set("gender")}>
                    <option value="">—</option><option value="feminino">Feminino</option><option value="masculino">Masculino</option><option value="outro">Outro</option>
                  </Select>
                </Fld>
                {editing === "new" && <Fld label="Senha (opcional)"><Input value={form.password} onChange={set("password")} type="password" placeholder="gerada se vazio" data-testid="crm-form-password" /></Fld>}
                <div className="sm:col-span-2"><Fld label="Observações"><Textarea value={form.notes} onChange={set("notes")} rows={2} /></Fld></div>
              </div>
            )}

            {tab === "endereco" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Fld label="CEP"><Input value={form.address.cep} onChange={setAddr("cep")} data-testid="crm-addr-cep" /></Fld>
                <Fld label="Cidade"><Input value={form.address.city} onChange={setAddr("city")} data-testid="crm-addr-city" /></Fld>
                <div className="sm:col-span-2"><Fld label="Rua / Logradouro"><Input value={form.address.street} onChange={setAddr("street")} /></Fld></div>
                <Fld label="Número"><Input value={form.address.number} onChange={setAddr("number")} /></Fld>
                <Fld label="Bairro"><Input value={form.address.district} onChange={setAddr("district")} /></Fld>
                <Fld label="Complemento"><Input value={form.address.complement} onChange={setAddr("complement")} /></Fld>
                <Fld label="UF"><Input value={form.address.state} onChange={setAddr("state")} maxLength={2} className="uppercase" /></Fld>
              </div>
            )}

            {tab === "docs" && (
              <div className="space-y-3">
                {editing === "new" ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Salve o cliente primeiro para anexar documentos.</p>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <Select value={docType} onChange={(e) => setDocType(e.target.value)}
                        aria-label="Tipo do documento" className="w-auto">
                        {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Select>
                      <label className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 border border-brand-200 hover:bg-brand-50 rounded-lg px-3 py-2 cursor-pointer">
                        <Upload size={16} /> {uploadingDoc ? "Enviando..." : "Anexar arquivo"}
                        {/* Input escondido atrás do label: o nome acessível vem
                            explícito, porque o texto do label é condicional. */}
                        <input type="file" onChange={uploadDoc} aria-label="Escolher arquivo para anexar"
                          className="hidden" data-testid="crm-doc-upload" />
                      </label>
                    </div>
                    <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                      {docs.map((d) => (
                        <div key={d.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid="crm-doc-row">
                          <a href={imgUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-brand-600">
                            <FileText size={15} /> <span className="font-medium">{d.type}</span> <span className="text-xs text-slate-400">{d.filename}</span>
                          </a>
                          <button onClick={() => removeDoc(d)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      ))}
                      {docs.length === 0 && <p className="px-3 py-6 text-sm text-slate-400 text-center">Nenhum documento anexado.</p>}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "compras" && editing !== "new" && (
              <PurchaseHistory customer={editing} onClose={() => setEditing(null)} />
            )}

        </Modal>
      )}

      {tagging && (
        <Modal open onClose={() => setTagging(null)} title={`Tags de ${tagging.name}`} maxWidth="max-w-md" testId="crm-tags-dialog">
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="separadas por vírgula" data-testid="crm-tags-input" />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {SUGGESTED_TAGS.map((t) => <button key={t} onClick={() => setTags(tags ? `${tags}, ${t}` : t)} className="text-[11px] font-semibold bg-slate-100 hover:bg-brand-50 hover:text-brand-700 rounded-full px-2 py-1">{t}</button>)}
            </div>
            <button onClick={saveTags} disabled={savingTags} data-testid="crm-tags-save" className="mt-4 w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg">{savingTags ? "Salvando..." : "Salvar tags"}</button>
        </Modal>
      )}
    </div>
  );
}
